import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { PATHS } from 'router/config';

interface IGrowthPortrait {
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  desiredPosition: string;
  /** Подпись уровня опыта — уже переведённая вызывающим. */
  seniorityLabel: string;
  /** Направление обучения: «кем я стану», а не «кем работаю». */
  directionTitle: string | null;
  /** Заполненность анкеты, 0..100 — прямой повод дописать. */
  completeness: number;
  skillsCount: number;
}

/**
 * Портрет студента (ТЗ 0039, доводка UX).
 *
 * Претензия владельца дословно: «Мой портрет тоже плохо виден. Хочу
 * посмотреть на себя: кто я, как я выгляжу, какая мне профессия». Данные были
 * — они лежали в анкете на /resume, куда с этого экрана не вело ничего.
 *
 * Пустые поля НЕ прячем, а показываем подсказкой «не заполнено» со ссылкой:
 * пустая карточка без объяснения читается как поломка, а не как приглашение
 * дописать анкету.
 */
export const GrowthPortrait = memo(
  ({
    firstName,
    lastName,
    photoUrl,
    desiredPosition,
    seniorityLabel,
    directionTitle,
    completeness,
    skillsCount,
  }: IGrowthPortrait) => {
    const { t } = useTranslation();
    const name = [firstName, lastName].filter(Boolean).join(' ');
    // Инициалы — запасной вариант вместо серого прямоугольника: фото есть не у
    // всех, и «нет фото» не повод показывать дыру.
    const initials =
      [firstName, lastName]
        .filter(Boolean)
        .map((part) => part.trim().charAt(0).toUpperCase())
        .join('') || '?';

    return (
      <Card>
        {photoUrl ? <Photo src={photoUrl} alt={name} /> : <Initials>{initials}</Initials>}

        <Body>
          <Name>{name || t('growth.portrait.noName')}</Name>
          <Position>
            {desiredPosition || t('growth.portrait.noPosition')}
            {seniorityLabel ? <Seniority>{seniorityLabel}</Seniority> : null}
          </Position>

          <Facts>
            {directionTitle ? (
              <Fact>
                <FactLabel>{t('growth.portrait.direction')}</FactLabel>
                <FactValue>{directionTitle}</FactValue>
              </Fact>
            ) : null}
            <Fact>
              <FactLabel>{t('growth.portrait.skills')}</FactLabel>
              <FactValue>{skillsCount}</FactValue>
            </Fact>
            <Fact>
              <FactLabel>{t('growth.portrait.completeness')}</FactLabel>
              <FactValue>{t('growth.portrait.percent', { n: completeness })}</FactValue>
            </Fact>
          </Facts>
        </Body>

        <Actions>
          <EditLink to={PATHS.resume}>{t('growth.portrait.edit')}</EditLink>
          {/* Полосу дозаполнения показываем только когда есть что дозаполнять:
              на 100% она превращается в упрёк без причины. */}
          {completeness < 100 ? (
            <Progress>
              <ProgressFill style={{ width: `${Math.max(0, Math.min(100, completeness))}%` }} />
            </Progress>
          ) : null}
        </Actions>
      </Card>
    );
  },
);

const Card = styled.section`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  border-radius: 12px;
  border: 1px solid ${Colors.gray};

  ${mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Photo = styled.img`
  width: 84px;
  height: 84px;
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
`;

const Initials = styled.span`
  width: 84px;
  height: 84px;
  flex: 0 0 auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 28, lineHeight: 34, weight: FontWeight.bold })}
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Name = styled.span`
  ${fontService.getFont({ size: 22, lineHeight: 28, weight: FontWeight.bold })}
`;

const Position = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Seniority = styled.span`
  padding: 2px 10px;
  border-radius: 12px;
  background: ${Colors.bg_gray};
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Facts = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const Fact = styled.div`
  display: flex;
  flex-direction: column;
`;

const FactLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const FactValue = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  ${mobile} {
    align-items: flex-start;
    width: 100%;
  }
`;

const EditLink = styled(Link)`
  padding: 8px 18px;
  border-radius: 20px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.btn_text};
    opacity: 0.9;
  }
`;

const Progress = styled.div`
  width: 140px;
  height: 6px;
  border-radius: 3px;
  background: ${Colors.gray};
  overflow: hidden;

  ${mobile} {
    width: 100%;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${Colors.marker_green};
`;
